import React, { useEffect, useRef, useState } from 'react';
import './ReactionRow.css';

/**
 * ReactionRow — the chip row that lives under a Bubble.
 *
 * Props:
 *   reactions:     { emoji: userId[] }     — current reactions object
 *   currentUserId: string                  — used to highlight chips the user has
 *                                            already reacted with
 *   onToggle:      (emoji) => void         — invoked when a chip is tapped
 *   variant:       'self' | 'other'        — controls chip tint (warm on self
 *                                            bubbles, white on other bubbles)
 *
 * F7.4 — wiggle: when a chip's emoji is brand new (not present in the prior
 * render), we mark it with `chip--just-added` for one render cycle and strip
 * the class on `animationend`. Under prefers-reduced-motion, the wiggle CSS
 * collapses to a fade-only via the @media block in ReactionRow.css.
 */
function ReactionRow({
  reactions,
  currentUserId,
  onToggle,
  variant = 'other',
}) {
  // Track which emojis we have already seen at least once. Anything in
  // `reactions` whose key isn't here is "new" and gets the wiggle class.
  const seenRef = useRef(new Set());
  // State for the set of "just-added" emojis. We must put it in state so
  // React re-renders the chip with the class, then strips it on animationend.
  const [justAdded, setJustAdded] = useState(() => new Set());

  const entries = reactions
    ? Object.entries(reactions).filter(([, users]) => users && users.length > 0)
    : [];

  // After each render, diff the current emoji set against `seenRef`.
  // New emojis → mark as just-added; record them as seen.
  useEffect(() => {
    if (entries.length === 0) {
      // Nothing visible — keep seen as-is. We deliberately don't reset
      // because emojis that flap (added → removed → re-added) should
      // re-wiggle on re-add; we handle that by removing them from `seen`
      // when their array empties.
      return;
    }
    const newlyAdded = [];
    const present = new Set();
    for (const [emoji] of entries) {
      present.add(emoji);
      if (!seenRef.current.has(emoji)) {
        newlyAdded.push(emoji);
        seenRef.current.add(emoji);
      }
    }
    // Forget emojis no longer present so a re-add wiggles again.
    for (const seen of Array.from(seenRef.current)) {
      if (!present.has(seen)) {
        seenRef.current.delete(seen);
      }
    }
    if (newlyAdded.length > 0) {
      setJustAdded((prev) => {
        const next = new Set(prev);
        for (const e of newlyAdded) next.add(e);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(reactions || {})]);

  const handleAnimationEnd = (emoji) => {
    setJustAdded((prev) => {
      if (!prev.has(emoji)) return prev;
      const next = new Set(prev);
      next.delete(emoji);
      return next;
    });
  };

  if (entries.length === 0) return null;

  return (
    <div
      className={`glacier-reactions glacier-reactions--${variant}`}
      data-testid="reactions"
    >
      {entries.map(([emoji, users]) => {
        const count = users.length;
        const mine = !!currentUserId && users.includes(currentUserId);
        const wiggle = justAdded.has(emoji);
        const cls =
          'glacier-react-chip' +
          (mine ? ' glacier-react-chip--mine' : '') +
          (wiggle ? ' glacier-react-chip--just-added' : '');
        return (
          <button
            type="button"
            key={emoji}
            className={cls}
            aria-label={`${emoji} reaction, ${count}${
              mine ? ', you reacted' : ''
            }`}
            aria-pressed={mine}
            onClick={() => onToggle && onToggle(emoji)}
            onAnimationEnd={() => handleAnimationEnd(emoji)}
          >
            <span className="glacier-react-chip__emoji" aria-hidden="true">
              {emoji}
            </span>
            <span className="glacier-react-chip__count">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

export default ReactionRow;
